<script setup lang="ts">
import { computed, defineProps, ref } from "vue";
import { useI18n } from "vue-i18n";
import { ActivationItemGroup } from "@/types/calculators";
import InfoIcon from "@/components/icons/InfoIcon.vue";
import AppTooltip from "@/components/AppTooltip.vue";

const props = defineProps<{
  resultedActivations: ActivationItemGroup;
}>();

const { t } = useI18n();

const tableRef = ref<HTMLElement>();

const hasFavourableForColumn = computed(() => {
  for (const resultedActivation of props.resultedActivations) {
    if (resultedActivation.especially_favorable_for !== null) {
      return true;
    }
  }
  return false;
});
</script>

<template>
  <div class="mb-6">
    <div class="rounded-[20px] overflow-y-auto">
      <table class="w-full text-left overflow-hidden" ref="tableRef">
        <thead
          class="bg-brown text-white text-sm leading-[16px] lg:text-xl lg:leading-[24px]"
        >
          <tr>
            <th
              class="font-normal lg:font-semibold pl-2 pr-1 lg:px-4 py-6 w-[200px]"
            >
              {{ t("calculators.activations.results.name") }}
            </th>
            <th class="th-item">
              {{ t("calculators.activations.results.date") }}
            </th>
            <th class="th-item">
              {{ t("calculators.activations.results.time") }}
            </th>
            <th class="th-item">
              {{ t("calculators.activations.results.sector") }}
            </th>
            <th class="font-normal lg:font-semibold pr-2 pl-1 lg:px-4 py-6">
              {{ t("calculators.activations.results.activators") }}
            </th>
            <th
              v-if="hasFavourableForColumn"
              class="font-normal lg:font-semibold pr-2 pl-1 lg:px-4 py-6"
            >
              {{
                t("calculators.activations.results.especially_favorable_for")
              }}
            </th>
            <th class="font-normal lg:font-semibold pr-2 pl-1 lg:px-4 py-6">
              {{ t("calculators.activations.results.not_suitable_for") }}
            </th>
          </tr>
        </thead>
        <tbody class="bg-white">
          <tr
            v-for="(activation, i) in resultedActivations"
            :key="i"
            class="border-t font-normal text-sm leading-[16px] lg:text-base lg:leading-[20px] text-black"
          >
            <td class="pl-2 pr-1 lg:px-4 py-2">
              <div class="flex gap-2 items-center">
                <span class="w-min">
                  {{
                    t(
                      `calculators.activations.activation_list.${activation.activation_name}`
                    )
                  }}
                </span>
                <AppTooltip
                  :containerRef="tableRef"
                  class="no-print flex-shrink-0"
                >
                  <template #trigger>
                    <InfoIcon class="size-6 text-active" />
                  </template>
                  <template #popper>
                    <div class="max-w-96 text-left">
                      {{
                        t(
                          `calculators.activations.activation_description.${activation.activation_name}`
                        )
                      }}
                    </div>
                  </template>
                </AppTooltip>
              </div>
            </td>
            <td class="td-item">{{ activation.date }}</td>
            <td class="td-item">
              <div>{{ activation.hour_symbol }}</div>
              <div>{{ activation.time_interval }}</div>
            </td>
            <td class="td-item">
              <template v-if="Array.isArray(activation.activation_sector)">
                <div
                  v-for="(item, index) in activation.activation_sector"
                  :key="index"
                  class="flex flex-col"
                >
                  <span>{{ t(`faq.sectors.mountains.${item.sector}`) }}</span>
                </div>
              </template>
              <template v-else>
                {{ t(`faq.sectors.mountains.${activation.activation_sector}`) }}
              </template>
            </td>
            <td class="pl-1 pr-2 lg:px-4 py-2">
              <div class="flex flex-col">
                <div
                  class="flex gap-2 items-center"
                  v-for="(activator, idx) in activation.activators"
                  :key="idx"
                >
                  <span class="w-min">
                    {{ t(`calculators.activations.activators.${activator}`) }}
                  </span>
                  <AppTooltip
                    :containerRef="tableRef"
                    class="no-print flex-shrink-0"
                  >
                    <template #trigger>
                      <InfoIcon class="size-6 text-active" />
                    </template>
                    <template #popper>
                      <div class="max-w-96 text-left">
                        {{
                          t(
                            `calculators.activations.faq.activators.${activator}`
                          )
                        }}
                      </div>
                    </template>
                  </AppTooltip>
                </div>
              </div>
            </td>
            <td v-if="hasFavourableForColumn" class="pl-1 pr-2 lg:px-4 py-2">
              <div
                v-for="(
                  favourableFor, idx
                ) in activation.especially_favorable_for"
                :key="idx"
              >
                {{ favourableFor }}
              </div>
            </td>
            <td class="td-item">{{ activation.not_suitable_for }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.td-item {
  @apply px-1 lg:px-4 py-2;
}

.th-item {
  @apply font-normal lg:font-semibold px-1 lg:px-4 py-6;
}
@media print {
  .no-print {
    display: none !important;
  }
}
</style>
